import { Box, GridItem, SimpleGrid, VStack } from '@chakra-ui/react';
import { usePropertiesModel } from '@common/models/PropertiesModel';
import { FontFamily } from '@common/theming/Fonts';
import currency from '@common/utility/currency';
import { SFRPropertyStatus } from '@services/appsync/types';
import React from 'react';
import { Link } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { ROUTES } from 'src/apps/invest/config';

type InvestCTAProps = {
  children?: React.ReactNode;
};

export const InvestCtaMobileSticky: React.FC<InvestCTAProps> = () => {
  const { property } = usePropertiesModel();
  const totalPropertyAmount = Number(property?.target_amount ?? 0);
  const currentAmountRaised = Number(property?.raised_amount?.total ?? 0);
  const fundAmountAvailable = totalPropertyAmount - currentAmountRaised;
  const fundedPercent = (currentAmountRaised / totalPropertyAmount) * 100;
  const fullyFunded = property?.sfr?.status.status === SFRPropertyStatus.SOLD_OUT;

  let totalInvestors = Number(property?.investor_count?.total);
  if (isNaN(totalInvestors)) totalInvestors = 0;

  return (
    <SimpleGrid columns={3} templateColumns={['max-content', 'max-content', 'auto']}>
      <GridItem colSpan={3}>
        <Box backgroundColor="gray.100" borderRadius={0} h={15} w={'full'}>
          <Box
            background={fullyFunded ? 'gray.500' : 'linear-gradient(270deg, #308EFF 0%, #034089 100%)'}
            borderTopRightRadius={15}
            borderBottomRightRadius={15}
            h={15}
            minW={fundedPercent < 5 ? '5%' : '0%'}
            w={`${fundedPercent}%`}></Box>
        </Box>
      </GridItem>
      <GridItem padding="30px" display="flex" justifyContent={'center'} alignItems={'center'}>
        {fullyFunded ? (
          <VStack alignItems={'flex-start'}>
            <TextBlock fontSize={'16px'} weight="bold" lineHeight={0.85}>
              {currency.format(totalPropertyAmount ?? 0, { maximumFractionDigits: 0 })}
            </TextBlock>
            <TextBlock fontSize={'12px'} color="gray.600" lineHeight={0.5}>
              RAISED
            </TextBlock>
          </VStack>
        ) : (
          <VStack alignItems={'flex-start'}>
            <TextBlock color="blue.500" fontSize={'16px'} weight="bold" lineHeight={0.85}>
              {currency.format(fundAmountAvailable ?? 0, { maximumFractionDigits: 0 })}
            </TextBlock>
            <TextBlock fontSize={'12px'} color="gray.600" lineHeight={0.5}>
              AVAILABLE
            </TextBlock>
          </VStack>
        )}
      </GridItem>
      <GridItem display="flex" justifyContent={'center'} alignItems={'center'}>
        <VStack alignItems={'flex-start'}>
          <TextBlock fontSize={'16px'} weight="bold" lineHeight={1}>
            {totalInvestors}
          </TextBlock>
          <TextBlock fontSize={'12px'} color="gray.600" lineHeight={0.5}>
            {Number(totalInvestors) === 1 ? 'INVESTOR' : 'INVESTORS'}
          </TextBlock>
        </VStack>
      </GridItem>
      <GridItem display="flex" justifyContent={'center'} alignItems={'center'}>
        {fullyFunded ? (
          <Box
            background={`gray.300`}
            borderRadius={'5px'}
            color={'white'}
            fontFamily={FontFamily.HELVETICA_NOW_BOLD}
            fontSize={'16px'}
            lineHeight={'40px'}
            textAlign={'center'}
            w={'full'}>
            Fully Funded
          </Box>
        ) : (
          <Box
            as={Link}
            to={property?.slug ? ROUTES.checkoutSFR(property.slug) : ''}
            _hover={{
              background: `linear-gradient(270deg, #308EFF 0%, #308EFF 100%);`,
              cursor: 'pointer',
            }}
            background={`linear-gradient(270deg, #308EFF 0%, #034089 100%);`}
            borderRadius={'5px'}
            backgroundColor={'blue.500'}
            color={'white'}
            fontFamily={FontFamily.HELVETICA_NOW_BOLD}
            fontSize={'16px'}
            lineHeight={'40px'}
            paddingX="20px"
            textAlign={'center'}>
            Invest
          </Box>
        )}
      </GridItem>
    </SimpleGrid>
  );
};
